'use client';
import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import { motion } from 'framer-motion';
import MuxPlayer from '@mux/mux-player-react';
import Link from 'next/link';

type Media = {
  url: string;
};

type Project = {
  id?: string | number;
  name?: string;
  slug: any;
  media?: {
    desktop_media?: Media[];
    mobile_media?: Media[];
    mux_video: any;
    enable_mux_video: boolean;
  };
};

type SectionsExploreOurProjectsProps = {
  __typename?: string;
  heading?: string;
  projects?: Project[];
  padding: {
    mobile_top_padding: any;
    mobile_bottom_padding: any;
    desktop_top_padding: any;
    desktop_bottom_padding: any;
  };
  button?: any;
};

const ComponentSectionsExploreOurProjects: React.FC<{
  data: SectionsExploreOurProjectsProps;
}> = ({ data }) => {
  const { __typename, heading = '', projects = [] } = data;
  const [top, setTop] = useState('');
  const [bottom, setBottom] = useState('');

  useEffect(() => {
    const updatePadding = () => {
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        setTop(data.padding?.mobile_top_padding ?? '');
        setBottom(data.padding?.mobile_bottom_padding ?? '');
      } else {
        setTop(data.padding?.desktop_top_padding ?? '');
        setBottom(data.padding?.desktop_bottom_padding ?? '');
      }
    };

    updatePadding(); // Run on mount

    window.addEventListener('resize', updatePadding); // Listen on resize
    return () => window.removeEventListener('resize', updatePadding); // Clean up
  }, [data]);
  return (
    <section
      id={__typename}
      className="pb-10 md:pb-20 border-t border-[#B6C38066] pt-4 md:pt-5"
      style={{
        paddingTop: `${top}rem`,
        paddingBottom: `${bottom}rem`,
      }}
    >
      <div className="grid grid-cols-12 items-start">
        {/* Animated Heading */}
        <motion.div
          className="col-span-12 md:col-span-3"
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, amount: 0.3 }}
        >
          <h3 className="text-[20px] md:text-[22px] text-[#E5E1D5] font-light leading-none mb-[10px] md:mb-[10px]">
            {heading}
          </h3>
          <Link
            href={data?.button?.button_url}
            className="text-[16px] text-[#B6C380] font-light leading-none mb-7 md:mb-5"
          >
            {data?.button?.button_text}
          </Link>
        </motion.div>

        <div className="col-span-12 md:col-span-9">
          <div className="ourprojects flex sm:grid sm:grid-cols-12 pb-4 sm:pb-0 gap-3 items-start overflow-x-auto md:overflow-x-visible w-[95.5vw] sm:w-auto">
            {projects.map((project, index) => {
              const imageUrl =
                project.media?.desktop_media?.[0]?.url ||
                project.media?.mobile_media?.[0]?.url ||
                '';
              const muxPlaybackId = project.media?.mux_video?.playback_id;
              const enableMuxVideo = project.media?.enable_mux_video;

              return (
                <motion.div
                  className="col-span-4 min-w-[285px] md:min-w-min"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true, amount: 0.3 }}
                  key={project.id || index}
                >
                  <Link href={`/projects/${project.slug}`} className="w-full">
                    {enableMuxVideo && muxPlaybackId ? (
                      <MuxPlayer
                        playbackId={muxPlaybackId}
                        autoPlay
                        muted
                        loop
                        playsInline
                        streamType="on-demand"
                        style={{ width: '100%' }}
                      />
                    ) : (
                      <>
                        {imageUrl && (
                          <Image
                            src={imageUrl}
                            alt={project.name || 'Project image'}
                            width={300}
                            height={400}
                            layout="responsive"
                            className="aspect-video w-full h-auto object-cover"
                            style={{ aspectRatio: '16 / 20', width: '100%' }}
                          />
                        )}
                      </>
                    )}
                    <p className="text-base mt-2 md:mt-3 text-[#E5E1D5] font-light leading-none">
                      {project.name}
                    </p>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ComponentSectionsExploreOurProjects;
